import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { fetchEntrySummary } from "core-integration/src/store/entries/thunks";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import { getSettings } from "core-integration/src/store/game/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { WebViewContext } from "plos/src/contexts/WebViewContext";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import {
  IEntryPoint,
  ITemplateType,
  useAdobeContext,
} from "../../contexts/AdobeContext";

interface RecapBaseProps {
  type: ITemplateType;
  redirectPath: string;
}

const RecapBase = ({ type, redirectPath }: RecapBaseProps) => {
  const { embed, getTemplate } = useAdobeContext();
  const { isWebView } = useContext(WebViewContext);

  const [searchParams] = useSearchParams();
  const postShare = searchParams.get("success");

  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useLocalizedNavigate();
  const now = useSelector(getCurrentEvent);
  const player = useSelector((state: RootState) => getPlayerData(state));
  const entry = useSelector((state: RootState) =>
    player?.entry ? getEntry(state, player.entry) : null,
  );
  const settings = useSelector(getSettings);

  const entryPoint = (() => {
    const entryPoint = searchParams.get("entryPoint")?.toLowerCase();
    if (entryPoint) {
      if (
        [
          "deep-link",
          "in-app-modal",
          "persistent-cta",
          "push-notification",
        ].includes(entryPoint)
      ) {
        return entryPoint as IEntryPoint;
      }
    }
  })();

  useEffect(() => {
    if (entry) {
      dispatch(fetchEntrySummary(entry.id));
    }
  }, [dispatch, entry?.id]);

  const getShareRedirectUrl = (success: "true" | "false") => {
    const params = new URLSearchParams(searchParams);
    params.set("success", success);

    return `${redirectPath}?${params.toString()}`;
  };

  const onShareError = () => {
    window.location.href = getShareRedirectUrl("false");
  };

  const onShareSuccess = () => {
    window.location.href = getShareRedirectUrl("true");
  };

  useEffect(() => {
    if (!isWebView) {
      navigate("/");
      return;
    }

    if (!settings?.static_content_url || !now || !entry) {
      return;
    }

    (async () => {
      const templateUrl = await getTemplate({
        baseUrl: settings?.static_content_url,
        entry,
        event: now,
        type,
      });

      if (!templateUrl) {
        navigate("/");
        return;
      }

      // Open the SDK automatically on first visit or after a previously failed share attempt
      if (!postShare || postShare === "false") {
        embed.viewImage({
          badgeHolder: Boolean(
            entry.club_badge_src && entry.club_badge_src !== "Pending",
          ),
          entryPoint,
          onImageShareError: onShareError,
          onImageShareSuccess: onShareSuccess,
          type,
          url: templateUrl,
        });
      }
    })();
  }, [
    embed,
    entry,
    entryPoint,
    getTemplate,
    isWebView,
    navigate,
    now,
    postShare,
    settings?.static_content_url,
  ]);

  if (!entry) {
    return null;
  }

  return <></>;
};

export default RecapBase;
